import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Select, message, Spin, Alert, Radio, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { schoolAPI, teacherAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeScopeId, resolveDefaultSchoolId, shouldLockSchoolSelection } from '../../utils/schoolScope';

const { Option } = Select;

const TeacherCreate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loginType, setLoginType] = useState('token'); // ✅ 新增：登录方式状态
  const { user } = useAuth();
  
  useEffect(() => {
    if (user) fetchSchools();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await schoolAPI.getAll();
      const availableSchools = response.data.data || [];
      setSchools(availableSchools);
      const defaultSchoolId = resolveDefaultSchoolId(user, availableSchools);
      if (defaultSchoolId) form.setFieldsValue({ schoolId: defaultSchoolId });
      setError(null);
    } catch (err) {
      console.error('获取学校列表失败:', err);
      setError('获取学校列表失败，请检查网络连接或重新登录');
    } finally {
      setLoading(false);
    }
  };

  // 表单提交
  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      
      // ✅ 添加登录方式
      values.loginType = loginType;
      
      // ✅ 验证账号密码模式的必填项
      if (loginType === 'password') {
        if (!values.username || !values.password) {
          message.error('账号密码登录需要填写用户名和密码');
          setSubmitting(false);
          return;
        }
        if (values.password.length < 6) {
          message.error('密码长度至少为6位');
          setSubmitting(false);
          return;
        }
      }
      
      const response = await teacherAPI.create(values);
      
      // ✅ 根据登录方式显示不同信息
      if (loginType === 'token') {
        Modal.success({
          title: '教师创建成功',
          content: (
            <div>
              <p>教师姓名：{values.name}</p>
              <p>登录口令：<strong style={{fontSize: 18, color: '#1890ff'}}>{response.data?.data?.token}</strong></p>
              <p style={{color: '#999', marginTop: 10}}>请妥善保管口令，用于桌面客户端和微信登录</p>
            </div>
          ),
          onOk: () => navigate('/teachers')
        });
      } else {
        Modal.success({
          title: '教师创建成功',
          content: (
            <div>
              <p>教师姓名：{values.name}</p>
              <p>登录账号：<strong>{values.username}</strong></p>
              <p>初始密码：<strong>{values.password}</strong></p>
              <p>口令：{response.data?.data?.token}</p>
              <p style={{color: '#999', marginTop: 10}}>可以使用账号密码登录管理后台，或使用口令登录桌面客户端</p>
            </div>
          ),
          onOk: () => navigate('/teachers')
        });
      }
    } catch (err) {
      console.error('创建教师失败:', err);
      setError('创建教师失败: ' + (err.response?.data?.message || err.message));
      message.error(err.response?.data?.message || '创建失败');
      setSubmitting(false);
    }
  };

  return (
    <Card title="添加教师">
      <Spin spinning={loading}>
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="name"
            label="教师姓名"
            rules={[{ required: true, message: '请输入教师姓名' }]}
          >
            <Input placeholder="请输入教师姓名" />
          </Form.Item>
          
          <Form.Item
            name="subject"
            label="任教科目"
          >
            <Input placeholder="请输入任教科目" />
          </Form.Item>
          
          <Form.Item
            name="schoolId"
            label="所属学校"
            rules={[{ required: true, message: '请选择所属学校' }]}
          >
            <Select
              placeholder="请选择所属学校"
              disabled={shouldLockSchoolSelection(user, schools)}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {schools.map(school => (
                <Option key={school.id} value={normalizeScopeId(school.id)}>{school.name}</Option>
              ))}
            </Select>
          </Form.Item>
          
          {/* ✅ 新增：登录方式选择 */}
          <Form.Item
            label="登录方式"
            required
          >
            <Radio.Group value={loginType} onChange={(e) => setLoginType(e.target.value)}>
              <Radio value="token">口令登录（桌面客户端/微信）</Radio>
              <Radio value="password">账号密码登录（管理后台）</Radio>
            </Radio.Group>
            <div style={{color: '#999', marginTop: 4, fontSize: 12}}>
              {loginType === 'token' ? '只生成6位数字口令，用于桌面客户端和微信小程序登录' : '同时创建账号密码，可以登录管理后台，也可以使用口令登录客户端'}
            </div>
          </Form.Item>
          
          {/* ✅ 新增：账号密码输入框（仅password模式显示） */}
          {loginType === 'password' && (
            <>
              <Form.Item
                name="username"
                label="登录用户名"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
                ]}
              >
                <Input placeholder="请输入用户名（字母、数字、下划线）" />
              </Form.Item>
              
              <Form.Item
                name="password"
                label="登录密码"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password placeholder="请输入密码（至少6位）" />
              </Form.Item>
            </>
          )}
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              创建教师
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate('/teachers')}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default TeacherCreate; 
